import { supabase } from '../lib/supabase';
import { propertyValidation } from '../utils/validationUtils';

export const propertyService = {
  async getAll(userId) {
    try {
      if (!userId) {
        throw new Error('معرف المستخدم مطلوب');
      }

      const { data, error } = await supabase?.from('properties')?.select('*, owners(*)')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching properties:', error);
        throw new Error(`فشل في تحميل العقارات: ${error?.message || 'خطأ غير معروف'}`);
      }

      if (!data) {
        return [];
      }

      return data?.map(row => ({
        id: row?.id,
        propertyId: row?.property_id,
        title: row?.title,
        type: row?.property_type,
        purpose: row?.purpose,
        status: row?.status,
        bedrooms: row?.bedrooms,
        bathrooms: row?.bathrooms,
        area: row?.area,
        floor: row?.floor,
        furnishing: row?.furnishing,
        yearBuilt: row?.year_built,
        remarks: row?.description,
        description: row?.description,
        features: row?.features || [],
        address: row?.address,
        city: row?.city,
        district: row?.district,
        lat: row?.latitude,
        lng: row?.longitude,
        latitude: row?.latitude,
        longitude: row?.longitude,
        price: row?.price,
        askingPrice: row?.asking_price,
        salePrice: row?.sale_price,
        currency: row?.currency,
        commission: row?.commission,
        clientRetainerPercentage: row?.client_retainer_percentage,
        ownerRetainerPercentage: row?.owner_retainer_percentage,
        negotiable: row?.negotiable,
        images: row?.images || [],
        videos: row?.videos || [],
        customFields: row?.custom_fields || {},
        owner: row?.owners?.name,
        ownerPhone: row?.owners?.phone,
        ownerEmail: row?.owners?.email,
        ownerId: row?.owner_id,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at,
        lastUpdate: row?.updated_at
      }));
    } catch (error) {
      console.error('Property service error:', error);
      throw error;
    }
  },

  async create(property, userId) {
    try {
      if (!userId) {
        throw new Error('معرف المستخدم مطلوب');
      }

      // SERVER-SIDE VALIDATION
      const validation = propertyValidation?.validate(property);
      if (!validation?.isValid) {
        const errorMessages = Object.values(validation?.errors)?.join('، ');
        throw new Error(`خطأ في البيانات: ${errorMessages}`);
      }

      if (!property?.title) {
        throw new Error('عنوان العقار مطلوب');
      }

      const { data, error } = await supabase?.from('properties')?.insert({
          user_id: userId,
          owner_id: property?.ownerId || null,
          property_id: property?.propertyId,
          title: property?.title,
          property_type: property?.propertyType,
          purpose: property?.purpose,
          status: property?.status || 'متاح',
          bedrooms: property?.bedrooms,
          bathrooms: property?.bathrooms,
          area: property?.area,
          floor: property?.floor,
          furnishing: property?.furnishing,
          year_built: property?.yearBuilt,
          description: property?.description,
          features: property?.features || [],
          address: property?.address,
          city: property?.city,
          district: property?.district,
          latitude: property?.latitude,
          longitude: property?.longitude,
          price: property?.price,
          asking_price: property?.askingPrice,
          sale_price: property?.salePrice,
          currency: property?.currency || 'JOD',
          commission: property?.commission,
          client_retainer_percentage: property?.clientRetainerPercentage,
          owner_retainer_percentage: property?.ownerRetainerPercentage,
          negotiable: property?.negotiable || false,
          images: property?.images || [],
          videos: property?.videos || [],
          custom_fields: property?.customFields || {}
        })?.select('*, owners(*)')?.single();

      if (error) {
        console.error('Error creating property:', error);
        throw new Error(`فشل في إنشاء العقار: ${error?.message || 'خطأ غير معروف'}`);
      }

      return {
        id: data?.id,
        propertyId: data?.property_id,
        title: data?.title,
        type: data?.property_type,
        status: data?.status,
        owner: data?.owners?.name,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Property service error:', error);
      throw error;
    }
  },

  async update(propertyId, updates, userId) {
    try {
      // SERVER-SIDE VALIDATION for updates
      if (Object.keys(updates)?.length > 0) {
        const validation = propertyValidation?.validate(updates);
        if (!validation?.isValid) {
          const errorMessages = Object.values(validation?.errors)?.join('، ');
          throw new Error(`خطأ في البيانات: ${errorMessages}`);
        }
      }

      const updateData = {};
      if (updates?.title) updateData.title = updates?.title;
      if (updates?.propertyType) updateData.property_type = updates?.propertyType;
      if (updates?.purpose) updateData.purpose = updates?.purpose;
      if (updates?.status) updateData.status = updates?.status;
      if (updates?.bedrooms !== undefined) updateData.bedrooms = updates?.bedrooms;
      if (updates?.bathrooms !== undefined) updateData.bathrooms = updates?.bathrooms;
      if (updates?.area !== undefined) updateData.area = updates?.area;
      if (updates?.floor !== undefined) updateData.floor = updates?.floor;
      if (updates?.furnishing) updateData.furnishing = updates?.furnishing;
      if (updates?.yearBuilt) updateData.year_built = updates?.yearBuilt;
      if (updates?.description) updateData.description = updates?.description;
      if (updates?.features) updateData.features = updates?.features;
      if (updates?.address) updateData.address = updates?.address;
      if (updates?.city) updateData.city = updates?.city;
      if (updates?.district) updateData.district = updates?.district;
      if (updates?.latitude) updateData.latitude = updates?.latitude;
      if (updates?.longitude) updateData.longitude = updates?.longitude;
      if (updates?.price !== undefined) updateData.price = updates?.price;
      if (updates?.askingPrice !== undefined) updateData.asking_price = updates?.askingPrice;
      if (updates?.salePrice !== undefined) updateData.sale_price = updates?.salePrice;
      if (updates?.currency) updateData.currency = updates?.currency;
      if (updates?.commission !== undefined) updateData.commission = updates?.commission;
      if (updates?.clientRetainerPercentage !== undefined) updateData.client_retainer_percentage = updates?.clientRetainerPercentage;
      if (updates?.ownerRetainerPercentage !== undefined) updateData.owner_retainer_percentage = updates?.ownerRetainerPercentage;
      if (updates?.negotiable !== undefined) updateData.negotiable = updates?.negotiable;
      if (updates?.images) updateData.images = updates?.images;
      if (updates?.videos) updateData.videos = updates?.videos;
      if (updates?.customFields) updateData.custom_fields = updates?.customFields;

      const { data, error } = await supabase?.from('properties')?.update(updateData)?.eq('id', propertyId)?.eq('user_id', userId)?.select('*, owners(*)')?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Property service error:', error);
      throw error;
    }
  },

  async delete(propertyId, userId) {
    const { error } = await supabase?.from('properties')?.delete()?.eq('id', propertyId)?.eq('user_id', userId);

    if (error) throw error;
  }
};